/**
 * Guided creation flow: a Form (WhatsApp Flow).
 *
 * Hangs off the Automations › Forms stop. The user ends the flow having actually
 * saved a form as a draft, with Name, Email and Mobile number fields already on
 * it and labelled — the same state a form sits in before it is shared as a link
 * or published to WhatsApp, which the closing step explains without pressing.
 *
 * ┌─ EVERYTHING HERE IS DRIVEN THROUGH EXISTING, UNMODIFIED UI ─────────────┐
 * │ The visual builder (`FlowVisualBuilder.tsx`) gives none of its rows,      │
 * │ menu items or field inputs a stable id/data-testid, and it is out of      │
 * │ scope for this module to add any — it is real product surface, not tour  │
 * │ chrome. Everything below resolves purely from label text, ARIA           │
 * │ relationships Radix already guarantees, and plain DOM counting — the      │
 * │ same class of technique `messageTemplate.ts` uses for its own two         │
 * │ label-only buttons.                                                       │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ ADD + LABEL RUNS IN `onNext`, NEVER `prepare` + `advance:'auto'` ──────┐
 * │ An earlier version drove this from `prepare` on an `advance:'auto'` step:│
 * │ auto-advance is gated ENTIRELY on `satisfied()` turning true, with no     │
 * │ time-based fallback (unlike an unresolved anchor, which the engine skips  │
 * │ on its own after a few seconds). If any single click in the sequence      │
 * │ below silently missed — a menu that took one render longer than usual, a  │
 * │ label text that no longer matched — `satisfied()` never flipped and the   │
 * │ user was stuck on that step forever with nothing visibly wrong.           │
 * │                                                                            │
 * │ `onNext` does not have that failure mode: `TaskEngine.advanceFromNext`    │
 * │ ALWAYS moves to the next step once the user presses Next, recording this  │
 * │ one as done or skipped depending on whether it actually landed. A failed  │
 * │ add is now an honest "skipped" in the tracker instead of a silent hang —  │
 * │ same trade `messageTemplate.ts`'s own steps already make.                 │
 * └────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ A RADIX MENU DOES NOT OPEN FROM `.click()` ────────────────────────────┐
 * │ This flow highlighted Add block and then added nothing, run after run,   │
 * │ and the reason was one line: `@radix-ui/react-dropdown-menu`'s trigger   │
 * │ toggles the menu from `onPointerDown` and declares NO `onClick` at all.  │
 * │ `element.click()` dispatches a click and nothing else, so the menu never │
 * │ opened, "Text answer" never existed, and every step of the sequence      │
 * │ below bailed out with only the ring left on screen. Everything that      │
 * │ presses a Radix control here goes through `pressWhenReady`/              │
 * │ `pressElement`, which send the whole pointer sequence a mouse does.      │
 * │ See `formFill.ts`'s `pointerish` for the full note.                      │
 * └────────────────────────────────────────────────────────────────────────┘
 */

import { isVisible, resolveAnchor, openAccordion, submenuAnchor, type AnchorSpec } from '../engine/anchors';
import {
  clickWhenReady,
  fillElement,
  hoverElement,
  findVisible,
  isFilled,
  pressElement,
  pressWhenReady,
  waitForAnchor,
  waitUntil,
} from './formFill';
import { bringIntoView, flashAnchor, flashElement } from './visibleAction';
import { closeFlowChrome } from './closeChrome';
import type { TaskFlow } from './types';

/** The Forms workspace's own route. */
const FORMS_PATH = '/home/flows';

/** Anything inside the form builder drawer. Scoped so a label can't match the page behind it. */
const DIALOG = '[role="dialog"], [role="alertdialog"]';

/**
 * A button identified by the words on it, scoped to a container.
 *
 * Weaker than an id and knowingly so — most of these buttons have none. A miss
 * makes the step skip rather than misfire, the same trade `messageTemplate.ts`
 * documents for its own two label-only buttons.
 */
const buttonLabelled = (label: string, within: string): AnchorSpec => ({
  structural: { within, match: 'button', labels: [label] },
});

/**
 * An item inside whichever dropdown/submenu is currently open, by its own text.
 *
 * Both the top-level "Add components" list and a nested category's submenu
 * (e.g. Text answer › Short answer) render every entry with `role="menuitem"`
 * inside a `role="menu"` container — that pairing is Radix's own menu
 * semantics, not anything this builder opted into, so it holds regardless of
 * how the menu's Tailwind classes change.
 */
const menuItemLabelled = (label: string): AnchorSpec => ({
  structural: { within: '[role="menu"]', match: '[role="menuitem"]', labels: [label] },
});

/** The Forms workspace's own "New form" button, scoped to the filter bar's action group. */
const NEW_FORM_BUTTON = buttonLabelled('New form', '[data-testid="automations-filter-bar-actions"]');

/** The "Add block" button that opens the field-type menu on the current screen. */
const ADD_BLOCK_BUTTON = buttonLabelled('Add block', DIALOG);

/**
 * Marks the ONE element the add-a-field sequence is acting on right now.
 *
 * ┌─ WHY THE RING HAS TO MOVE, AND WHY AN ATTRIBUTE IS HOW ────────────────────┐
 * │ 🔴 REPORTED AS "it is highlighting the whole form instead of the field it   │
 * │ is guiding". The ring is a 2px accent outline plus a 6px glow, and it sat   │
 * │ on the Add block button for the WHOLE sequence — a button that spans the    │
 * │ full width of the editor, so a rectangle round it reads as a rectangle      │
 * │ round the form. Meanwhile the thing being talked about (the menu, then the  │
 * │ category, then the row that appears below) was marked only by               │
 * │ `flashElement`'s brief pulse. The strongest mark on screen was pointing at  │
 * │ the least specific thing.                                                   │
 * │                                                                            │
 * │ A step carries ONE anchor and `TaskEngine.resolve()` re-runs it every       │
 * │ `TICK_MS`, so the way to move the ring is to make that one anchor resolve   │
 * │ somewhere new. The sequence therefore stamps this attribute on whatever it  │
 * │ is touching, and the anchor prefers it — `selector` is tried before         │
 * │ `structural`, so the marker wins whenever it exists and the Add block       │
 * │ button is the fallback the moment it does not.                             │
 * │                                                                            │
 * │ 🔴 THE SCROLLING COMES FREE, AND THAT IS THE SECOND HALF OF THE REPORT      │
 * │ ("scroll up when the content is down"). `resolve()` calls `scrollIntoView`  │
 * │ with `block: 'center'` whenever the resolved element CHANGES — so every     │
 * │ time the marker moves, the engine brings the new target into view by        │
 * │ itself. Nothing here has to scroll anything; it only has to move the mark.  │
 * │                                                                            │
 * │ An ATTRIBUTE rather than a remembered reference, for the reason             │
 * │ `openFieldLabels` spells out: the builder re-keys a row from its own label  │
 * │ the moment that label commits, so a held element can be replaced mid-       │
 * │ sequence. A stale attribute on a detached node simply stops matching, and   │
 * │ the anchor falls back — where a stale reference would pin the ring to a     │
 * │ node no longer in the document.                                            │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
const FOCUS_ATTR = 'data-tour-form-focus';

/** Move the mark — and therefore the ring — onto one element. */
function focusOn(el: Element | null | undefined): void {
  clearFormFocus();
  el?.setAttribute(FOCUS_ATTR, '');
}

/**
 * Take the mark off, so the ring falls back to the Add block button.
 *
 * Found by attribute rather than remembered, and safe to call at any time —
 * same policy as `releaseRoom`. Called on entering each add step (so a field
 * marked by the PREVIOUS one cannot still be ringed while the next step is
 * being read) and in `beforeStart`, since this module is a long-lived singleton
 * and a mark left by an abandoned run would outlive it.
 */
function clearFormFocus(): void {
  document.querySelectorAll(`[${FOCUS_ATTR}]`).forEach((el) => el.removeAttribute(FOCUS_ATTR));
}

/**
 * What the three add-a-field steps ring.
 *
 * The marked element while the sequence is running; the Add block button before
 * it starts and after anything goes wrong. See `FOCUS_ATTR`.
 */
const ADD_FIELD_TARGET: AnchorSpec = {
  selector: `[${FOCUS_ATTR}]`,
  structural: ADD_BLOCK_BUTTON.structural,
};

/** The "Text answer" category inside the open Add block menu, and the Short answer item inside it. */
const TEXT_ANSWER_ITEM = menuItemLabelled('Text answer');
const SHORT_ANSWER_ITEM = menuItemLabelled('Short answer');

/** The sidebar row that opens the Forms workspace. */
const SIDEBAR_ROW = submenuAnchor(['Automations'], 'Forms');

/**
 * Are we on the Forms workspace, with its New form button ready?
 *
 * The route commits before the page renders, so the path alone would advance to a
 * screen with nothing on it yet — the button check is kept alongside it for the
 * same reason `messageTemplate.ts`'s `onLibraryPage` keeps both.
 */
const onFormsPage = () =>
  window.location.pathname.startsWith(FORMS_PATH) && Boolean(resolveAnchor(NEW_FORM_BUTTON));

/** Is the builder drawer on screen? Its name field is the signal — it mounts the instant the drawer opens. */
const composerOpen = () => Boolean(findVisible('#flow-name-visual'));

/** Has this draft been saved at least once? The Delete button only renders once a record exists. */
const isSaved = () => Boolean(resolveAnchor(buttonLabelled('Delete', DIALOG)));

/**
 * Every visible field row's own accordion trigger, in DOM order.
 *
 * 🔴 `aria-controls` ALONE IS NOT ENOUGH, AND THAT WAS A BUG. Radix's Select,
 * Popover and DropdownMenu triggers all set `aria-controls` too (verified
 * against `@radix-ui/react-select`, `-popover` and `-dropdown-menu`) — so the
 * instant a field row is expanded, its "Input type" Select trigger (and, for a
 * text field, the Label input's emoji-picker Popover trigger) start matching
 * `button[aria-controls]` right alongside the row's own accordion trigger.
 * Once the first field is added and expanded, counting by that selector alone
 * over-counted, so the position math below (which assumes one entry per row)
 * pointed at the wrong element for the second field's row and its "input"
 * lookup found nothing — the second field silently failed to appear.
 *
 * `data-orientation` is what `AccordionTrigger` sets alongside `aria-controls`
 * (`"data-orientation": accordionContext.orientation` in
 * `@radix-ui/react-accordion`) that a Select/Popover/DropdownMenu trigger never
 * does — it is an Accordion/Tabs-only concept. Requiring both together is what
 * actually isolates the row headers, still with no id/testid asked of the
 * product's own markup. Scoped to the dialog so a coincidental match elsewhere
 * on the page can never count.
 */
function fieldRowTriggers(): HTMLElement[] {
  const dialogs = Array.from(document.querySelectorAll<HTMLElement>(DIALOG));
  return dialogs
    .flatMap((dialog) =>
      Array.from(dialog.querySelectorAll<HTMLElement>('button[data-orientation][aria-controls]')),
    )
    .filter(isVisible);
}

/**
 * The whole row (Radix's `AccordionItem`) a trigger belongs to, for pulsing a
 * newly added field rather than just its header.
 *
 * The item carries `data-state` AND `data-orientation` the same way the trigger
 * does; `closest` from the trigger walks past its `<h3>` header wrapper to it.
 * Falls back to the trigger, which is always a real element to mark.
 */
function rowOf(trigger: HTMLElement): HTMLElement {
  return trigger.closest<HTMLElement>('div[data-state][data-orientation]') ?? trigger;
}

/**
 * A row's expanded body, or null while it is collapsed.
 *
 * Radix's Accordion content is ABSENT from the DOM while closed, not merely
 * hidden — so this returning null is the normal state of a row nobody has
 * opened, not a failure.
 */
function contentOf(trigger: HTMLElement): HTMLElement | null {
  const id = trigger.getAttribute('aria-controls');
  const content = id ? document.getElementById(id) : null;
  return content ?? null;
}

/** Open a row if it is not already open, and wait for its body to mount. */
async function expandRow(trigger: HTMLElement): Promise<HTMLElement | null> {
  if (trigger.getAttribute('data-state') !== 'open') trigger.click();
  const opened = await waitUntil(() => Boolean(contentOf(trigger)?.querySelector('input')), 2500);
  return opened ? contentOf(trigger) : null;
}

/**
 * The Label input inside an expanded field row.
 *
 * First input in the body, which for every answer block is the Label — the
 * emoji picker beside it is a `<button>` and the Required toggle is a Radix
 * `Switch` (also a button), so neither can be mistaken for it.
 */
function labelInputOf(content: HTMLElement | null): HTMLInputElement | null {
  return content?.querySelector('input') ?? null;
}

/**
 * The label currently on each field row, for rows that are open.
 *
 * 🔴 THE FLOW CANNOT MARK A ROW AND EXPECT THE MARK TO SURVIVE. The builder
 * keys each row by `${component.name}-${index}` and DERIVES the name from the
 * label the moment the Label input blurs — so committing "Name" changes that
 * row's React key from `text_input-1` to `name-1`, which unmounts the row and
 * mounts a new one. Any `data-tourguide-target` attribute written onto the old
 * input goes with it. Reading the value back out is the only check that
 * survives the very edit it is checking for.
 */
function openFieldLabels(): string[] {
  return fieldRowTriggers()
    .map((trigger) => labelInputOf(contentOf(trigger))?.value.trim().toLowerCase() ?? '')
    .filter(Boolean);
}

/** Is there a field on this screen already carrying this label? */
function hasFieldLabelled(label: string): boolean {
  return openFieldLabels().includes(label.trim().toLowerCase());
}

/**
 * Point the row's "Input type" Select at a specific type (phone, email, …).
 *
 * Best-effort by design: a miss leaves the field as plain text, which is a
 * working field — so this NEVER blocks the step, and the step's copy explains
 * the control rather than claiming the guide set it.
 *
 * Radix's `Select` is driven the same way its menu is: the trigger opens from
 * `onPointerDown` for a mouse and the option commits on `pointerup`, neither of
 * which a bare `.click()` produces. `pressElement` sends the whole sequence.
 */
async function setInputType(content: HTMLElement, value: string): Promise<boolean> {
  const trigger = content.querySelector<HTMLElement>('button[role="combobox"]');
  if (!trigger) return false;
  if (trigger.getAttribute('data-state') !== 'open') pressElement(trigger);

  const wanted = value.toLowerCase();
  const findOption = () =>
    Array.from(document.querySelectorAll<HTMLElement>('[role="option"]')).find(
      (option) => (option.textContent ?? '').trim().toLowerCase() === wanted,
    ) ?? null;

  const appeared = await waitUntil(() => Boolean(findOption()), 2000);
  const option = appeared ? findOption() : null;
  if (!option) return false;

  await flashElement(option, { holdMs: 320 });
  // 🔴 HOVER FIRST, OR THE PRESS DOES NOTHING. Radix's Select tracks the item
  // under the pointer from `pointermove`, and an item it has never seen the
  // pointer reach does not answer a press at all — the list simply closes with
  // the value unchanged. Reproduced with no tour running, driving the builder
  // by hand: the flow's exact press sequence opened the Input type list, found
  // the `phone` option and pressed it, and the trigger still read `text`
  // afterwards. That is why the field the flow adds for a mobile number was
  // left as plain text (and, in the run before it, e-mail as text too).
  // `pressWhenReady` has always hovered for this reason; this call site was the
  // one that did not.
  hoverElement(option);
  pressElement(option);
  return waitUntil(() => (trigger.textContent ?? '').trim().toLowerCase() === wanted, 1500);
}

/** A field this flow adds, in the order it adds them. */
interface FieldSpec {
  /** Step id, and the key under which a successful add is remembered. */
  id: string;
  /** What the customer will see above the box, and what the guide types in. */
  label: string;
  /**
   * The Meta input type to switch the block to, when plain text is not the
   * right one. Matches the option text in the row's own "Input type" dropdown.
   */
  inputType?: string;
}

/**
 * The label every newly added Short answer block arrives with, from the
 * builder's own component template (`componentTemplates.ts`).
 *
 * 🔴 A NEW BLOCK IS NOT EMPTY, which is why the guide's label has to be allowed
 * to OVERWRITE. `fillField`'s `onlyIfEmpty` default — right everywhere else,
 * because it is what stops the flow retyping over the user's own edits —
 * silently declined every one of these: the field already said "Text input", so
 * the flow left it, and three fields called "Text input" also collide on the
 * name Meta derives from the label. Overwriting is gated on the value still
 * BEING this template default (or already being what we are about to write),
 * so a label the user has since edited themselves is still never touched.
 */
const TEMPLATE_LABEL = 'text input';

/**
 * Fields already added THIS RUN of the flow, so a step re-entered (e.g. Back
 * then Next again) never adds a second field on top of the first. Cleared in
 * `beforeStart`, since this module is a long-lived singleton and the flow can
 * be run more than once in one page session.
 */
const addedThisRun = new Set<string>();

/**
 * Add one short-answer field through the real Add block menu, at a speed the
 * user can follow, and label it.
 *
 * 🔴 EVERY STAGE IS SHOWN BEFORE IT HAPPENS. The previous version fired the
 * three clicks back to back with the ring left behind on Add block, so even
 * when it worked there was nothing to watch: the menu, the category and the
 * item came and went inside a frame. Each stage now pulses its target
 * (`flashAnchor`), holds, and only then presses — and the row that appears is
 * scrolled to and pulsed as well, because it is created BELOW the button the
 * ring is still sitting on and is otherwise the one part of the whole sequence
 * the user cannot see happening.
 *
 * Every failure point logs and returns rather than throwing — `onNext` errors
 * are swallowed by the engine either way, but a named warning is what makes a
 * silent miss diagnosable from the console instead of just "the field never
 * showed up".
 */
async function addShortAnswerField(field: FieldSpec): Promise<void> {
  if (addedThisRun.has(field.id) || hasFieldLabelled(field.label)) {
    addedThisRun.add(field.id);
    return;
  }

  const before = fieldRowTriggers();

  // The ring starts where the press does. A mark left by the PREVIOUS field's
  // sequence would otherwise still be on that field while this one runs.
  focusOn(resolveAnchor(ADD_BLOCK_BUTTON)?.element);

  // 1. Open the Add block menu. `pressWhenReady`, NOT `clickWhenReady`: Radix's
  //    dropdown trigger opens from `onPointerDown` and has no click handler at
  //    all, so the old bare `.click()` here was the reason this whole sequence
  //    ended at the highlight — see `pressElement`.
  if (!(await pressWhenReady(ADD_BLOCK_BUTTON))) {
    console.warn('[tour-guide] "create-form": could not find/press the Add block button.');
    return;
  }
  if (!(await waitForAnchor(TEXT_ANSWER_ITEM, 2500))) {
    console.warn('[tour-guide] "create-form": the Add block menu never opened.');
    return;
  }

  // 2. Walk down the menu, showing each stop. The mark moves with the walk, so
  //    the ring leaves the full-width button and lands on the actual item being
  //    chosen — which is the whole point of showing the walk at all.
  focusOn(resolveAnchor(TEXT_ANSWER_ITEM)?.element);
  await flashAnchor(TEXT_ANSWER_ITEM);
  if (!(await pressWhenReady(TEXT_ANSWER_ITEM))) return;
  if (!(await waitForAnchor(SHORT_ANSWER_ITEM, 2500))) {
    console.warn('[tour-guide] "create-form": the Text answer submenu never opened.');
    return;
  }
  focusOn(resolveAnchor(SHORT_ANSWER_ITEM)?.element);
  await flashAnchor(SHORT_ANSWER_ITEM);
  if (!(await pressWhenReady(SHORT_ANSWER_ITEM))) return;

  // 3. Find the row that just appeared. Preferring the element that was not
  //    there a moment ago is exact; the position fallback covers the case where
  //    React re-keys several rows at once (adding a block renumbers the Footer,
  //    which remounts it), leaving more than one node looking new.
  const grew = await waitUntil(() => fieldRowTriggers().length > before.length, 4000);
  if (!grew) {
    console.warn('[tour-guide] "create-form": the new field row never appeared.');
    return;
  }
  const after = fieldRowTriggers();
  const fresh = after.filter((trigger) => !before.includes(trigger));
  // A new block is appended and `ensureFooter` immediately pins the Footer back
  // to last, so it lands where the Footer used to be: the old row count, 0-indexed.
  const index = fresh.length === 1 ? after.indexOf(fresh[0]) : Math.max(before.length - 1, 0);
  const trigger = after[index] ?? after[after.length - 1];
  if (!trigger) return;

  // 4. Show it. This is the part the user asked to see: a row that was not
  //    there before, brought into view and marked, before anything is typed.
  const row = rowOf(trigger);
  // 🔴 MARK IT, THEN LET THE ENGINE SCROLL. `bringIntoView` still runs because
  // the row can be below the fold RIGHT NOW and the pulse below is worth seeing
  // immediately; the mark is what keeps it in view afterwards, since the engine
  // re-centres on every change of resolved element and would otherwise carry the
  // view back to whatever the ring was on before.
  focusOn(row);
  bringIntoView(row);
  await flashElement(row, { holdMs: 900, row: true });

  // 🔴 RE-RESOLVE BY POSITION IF THE NODE HAS GONE. Adding a block renumbers
  // every row after it, and the builder derives each row's React key from that
  // number — so a re-render during the pulse above can replace this exact
  // element with an equivalent one, and everything below (which reaches through
  // `aria-controls`) would then be reading a node that is no longer in the
  // document. Position survives what identity does not.
  const live = trigger.isConnected ? trigger : fieldRowTriggers()[index];
  if (!live) {
    console.warn('[tour-guide] "create-form": lost the new field row before it could be labelled.');
    return;
  }

  const content = await expandRow(live);
  if (!content) {
    console.warn('[tour-guide] "create-form": the new field row would not open.');
    return;
  }
  bringIntoView(content);

  // 5. Input type BEFORE the label. Committing the label re-keys the row and
  //    remounts it, which would throw away the `content` element this needs.
  if (field.inputType) await setInputType(content, field.inputType);

  const input = labelInputOf(contentOf(live) ?? content);
  if (!input) {
    console.warn("[tour-guide] \"create-form\": could not find the new field's Label input.");
    return;
  }

  // The ring finishes on the field's own Label input rather than back on the
  // button: the step's copy is about the field, so that is what should still be
  // circled when the user looks up from reading it.
  focusOn(input);
  await flashElement(input, { holdMs: 380 });
  const current = input.value.trim().toLowerCase();
  const writable = current === '' || current === TEMPLATE_LABEL || current === field.label.toLowerCase();
  if (writable) fillElement(input, field.label, { onlyIfEmpty: false });

  addedThisRun.add(field.id);
}

const two = (n: number) => String(n).padStart(2, '0');

/**
 * A name that reads as obviously throwaway and is distinct per run.
 *
 * Nothing here is validated against existing forms the way a template name is
 * against Meta's rules, but a fixed string would still look identical across
 * repeated runs in the same sandbox.
 */
function suggestedName(): string {
  const now = new Date();
  return `Product inquiry ${two(now.getHours())}${two(now.getMinutes())}${two(now.getSeconds())}`;
}

/**
 * Resolved ONCE, when the module loads — see `messageTemplate.ts`'s identical
 * comment on why this cannot be called per attempt.
 */
const SUGGESTED_NAME = suggestedName();

/** The three fields the flow puts on the form, in order. */
const NAME_FIELD: FieldSpec = { id: 'add-name-field', label: 'Name' };
const EMAIL_FIELD: FieldSpec = { id: 'add-email-field', label: 'Email', inputType: 'email' };
const MOBILE_FIELD: FieldSpec = { id: 'add-mobile-field', label: 'Mobile number', inputType: 'phone' };

/** Did this run put this field on the form? Falls back to reading it off the row. */
const fieldPresent = (field: FieldSpec) => () =>
  addedThisRun.has(field.id) || hasFieldLabelled(field.label);

export const CREATE_FORM_TASK: TaskFlow = {
  id: 'create-form',
  stepId: 'automations--forms',
  cta: 'Try it: build a form',
  preparingLabel: 'Opening Forms…',
  title: 'Create a form',
  summary: 'Name, Email and Mobile number fields added for you — you press the buttons.',
  doneTitle: 'Form saved',
  doneDescription:
    'It is in your Forms list now, marked Draft, with Name, Email and Mobile number fields on it. Share it as a link or drop it into an automation once you are happy with the fields — submissions land in the CRM as leads. Publish to WhatsApp whenever you are ready to put it in front of customers.',

  /**
   * Get to the Forms workspace BEFORE the flow's own chrome appears — no ring,
   * no callout, no bottom bar for this part. Mirrors `messageTemplate.ts`'s
   * `beforeStart` exactly, including why the sidebar group is re-opened.
   */
  beforeStart: async () => {
    addedThisRun.clear();
    // A mark left on the host's markup by an abandoned run would still be
    // ringed by the first add step of this one. Same reversibility rule the
    // rest of this module follows — nothing it stamps may outlive the flow.
    clearFormFocus();
    if (onFormsPage()) return;
    openAccordion(['Automations']);
    await clickWhenReady(SIDEBAR_ROW);
    const arrived = await waitUntil(onFormsPage, 5000);
    if (!arrived) {
      console.warn(
        '[tour-guide] "create-form": never confirmed arrival on /home/flows ' +
          '(route and/or New form button). The next step may resolve the wrong button.',
      );
    }
  },

  /**
   * Close the form composer this flow opened — see `TaskFlow.afterEnd` and
   * `closeChrome.ts`.
   *
   * 🔴 SAFE EVEN AFTER THE FORM HAS BEEN SAVED, and that is worth stating because
   * this is the one flow here whose composer can be sitting on a REAL record
   * (`isSaved()` is exactly that test). Closing the composer is not deleting the
   * form — the record stays in the list either way, which is where the tour is
   * about to point. Before it is saved there is nothing to lose but the blocks the
   * guide added itself.
   *
   * Without this, Skip module resumed the tour with the composer still up: a Radix
   * modal with `pointer-events: none` on the body and a backdrop over the page, so
   * the next module was highlighted underneath it.
   */
  afterEnd: async () => {
    await closeFlowChrome(composerOpen);
  },

  steps: [
    {
      id: 'open-composer',
      label: 'New form',
      title: 'Start a new form',
      description:
        'This is your Forms workspace — every form you have, shared as a link or dropped into an automation. New form opens the builder. Press Next to open it.',
      anchor: NEW_FORM_BUTTON,
      side: 'bottom',
      spotlight: true,
      // See `messageTemplate.ts`'s identical comment on `ready` + a no-op `prepare`:
      // both are load-bearing here for the same cross-page-button-label reason.
      ready: onFormsPage,
      prepare: async () => {},
      onNext: async () => {
        if (composerOpen()) return;
        await clickWhenReady(NEW_FORM_BUTTON);
        // 🔴 WAIT FOR THE DRAWER, DO NOT JUST PRESS THE BUTTON.
        // `clickWhenReady` resolves the moment the click is DISPATCHED, and the
        // builder is a lazy route chunk plus a mount — so `satisfied` was read
        // while the composer was still on its way and this step was recorded as
        // SKIPPED on every single run. The cost was not cosmetic: the completion
        // panel counts skipped steps and says "reached the end — 1 step skipped,
        // so this may not have been created", which it then showed next to
        // "Form saved" on a form that had just been saved. Reported from a
        // screenshot of exactly that pair.
        await waitUntil(composerOpen, 5000);
      },
      satisfied: composerOpen,
      /**
       * Back over this step closes what its Next opened — see `TaskStep.rewind`.
       *
       * Without it, Back from the step after this one landed HERE with the
       * form builder still covering the New form
       * button this step is about, which `resolve()` then refuses to ring: a step on screen with nothing
       * highlighted, which is what "Back disturbs the tour" looks like.
       */
      rewind: async () => {
        await closeFlowChrome(composerOpen, 'rewind');
      },
      waitingHint: 'Press Next to open the builder.',
    },

    {
      id: 'name',
      label: 'Name',
      title: 'Name it',
      description:
        'Give the form a name — this is what you and your team see in the Forms list, not something a customer reads. Pick something that says what it collects.',
      anchor: { selector: '#flow-name-visual' },
      side: 'right',
      spotlight: true,
      fill: [['#flow-name-visual', SUGGESTED_NAME]],
      satisfied: () => isFilled('#flow-name-visual'),
      waitingHint: 'Type a name to carry on.',
    },

    {
      id: 'add-name-field',
      label: 'Name field',
      title: 'Add a Name field',
      description:
        'Add block is how every field gets onto the screen. Press Next and watch — the guide opens the menu, picks Text answer › Short answer, and the new field appears on the screen below, labelled Name for you.',
      anchor: ADD_FIELD_TARGET,
      side: 'right',
      /**
       * Clears the mark the PREVIOUS field's sequence finished on, so this step
       * is read with the ring back on Add block rather than still circling the
       * field before it. Also what makes `enter()` wait for and show the target
       * before `onNext` acts on it — see its own note.
       */
      prepare: async () => { clearFormFocus(); },
      // 🔴 NO SPOTLIGHT ON THE THREE ADD STEPS. The spotlight paints everything
      // OUTSIDE the ring's rect dark — and everything this step does happens
      // outside it: the dropdown opens below the button, and the new field row
      // lands further down the panel. Dimming exactly the thing the user was
      // asked to watch is the opposite of what the step is for. The ring plus
      // the travelling pulse (`flashElement`) carry the attention instead.
      onNext: () => addShortAnswerField(NAME_FIELD),
      satisfied: fieldPresent(NAME_FIELD),
      waitingHint: 'Press Next to add the field.',
    },

    {
      id: 'add-email-field',
      label: 'Email field',
      title: 'Add an Email field',
      description:
        'Same again for an email address. Input type is what tells WhatsApp how to check the answer — the guide sets this one to email, so a reply without an @ is rejected before it reaches you.',
      anchor: ADD_FIELD_TARGET,
      side: 'right',
      /**
       * Clears the mark the PREVIOUS field's sequence finished on, so this step
       * is read with the ring back on Add block rather than still circling the
       * field before it. Also what makes `enter()` wait for and show the target
       * before `onNext` acts on it — see its own note.
       */
      prepare: async () => { clearFormFocus(); },
      onNext: () => addShortAnswerField(EMAIL_FIELD),
      satisfied: fieldPresent(EMAIL_FIELD),
      waitingHint: 'Press Next to add the field.',
    },

    {
      id: 'add-mobile-field',
      label: 'Mobile field',
      title: 'Add a Mobile number field',
      description:
        'One more — a short answer for their mobile number, with Input type set to phone so WhatsApp shows a number pad and validates what they type. Three fields is plenty for a first form.',
      anchor: ADD_FIELD_TARGET,
      side: 'right',
      /**
       * Clears the mark the PREVIOUS field's sequence finished on, so this step
       * is read with the ring back on Add block rather than still circling the
       * field before it. Also what makes `enter()` wait for and show the target
       * before `onNext` acts on it — see its own note.
       */
      prepare: async () => { clearFormFocus(); },
      onNext: () => addShortAnswerField(MOBILE_FIELD),
      satisfied: fieldPresent(MOBILE_FIELD),
      waitingHint: 'Press Next to add the field.',
    },

    {
      id: 'save',
      label: 'Save',
      title: 'Save the draft',
      description:
        'Click Save draft. It lands in your Forms list marked Draft — nothing is shared or published until you choose to, so this is always safe to press.',
      anchor: buttonLabelled('Save draft', DIALOG),
      side: 'top',
      spotlight: true,
      advance: 'auto',
      // The drawer stays open after a save (so you can keep editing), unlike the
      // template composer this pattern is modelled on — so success is read from
      // the Delete button appearing, not from the drawer closing.
      satisfied: isSaved,
      waitingHint: 'Waiting for you to press Save draft…',
      optional: false,
    },

    {
      id: 'publish',
      label: 'Publish',
      title: 'Publish to WhatsApp',
      description:
        'This is the button that sends the form to Meta and makes it usable in a WhatsApp conversation. It needs a saved draft, a connected WhatsApp Business account and a form with no errors — until then it stays greyed out. Press it yourself when you are ready; a published form cannot be edited freely afterwards.',
      anchor: buttonLabelled('Publish to WhatsApp', DIALOG),
      side: 'top',
      spotlight: true,
      // 🔴 EXPLAINED, NEVER PRESSED FOR THEM — the one step in this flow with no
      // `onNext`. Every other action here is reversible inside the user's own
      // account; publishing creates a live flow at Meta under the business's
      // name and cannot be quietly undone, so it is the user's call and nobody
      // else's. That is also why there is no `satisfied`: the goal of this step
      // is to be READ. Pressing Next means "understood", and the tracker says
      // done rather than warning that something was skipped — because nothing
      // was.
      nextLabel: 'Finish',
    },
  ],
};
